import React, { useEffect, useMemo, useState, useRef, Children, Fragment, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { RefreshButton } from '../shared/RefreshButton';
import { useGithubData } from '../../hooks/useGithubData';
import { useProjectContext } from '../../hooks/useProjectContext';
import { GitPullRequestIcon, GitCommitIcon, GitBranchIcon, PlayCircleIcon, CheckCircle2Icon, XCircleIcon, ClockIcon, AlertCircleIcon, ExternalLinkIcon, GithubIcon, ShieldIcon, XIcon, ActivityIcon, SearchIcon, ArrowLeftIcon, StarIcon, CircleDotIcon, UsersIcon, CodeIcon, CalendarIcon, ChevronDownIcon, DownloadIcon, CopyIcon, CheckIcon, FilterIcon, FileIcon, GitMergeIcon, ChevronRightIcon, TagIcon, ArrowRightIcon, MessageSquareIcon, BookOpenIcon, FileTextIcon, Maximize2Icon, Minimize2Icon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PRReviewPanel } from './PRReviewPanel';
import { githubData, GitHubPRFile } from '../../services/githubData';
import { SideBySideDiff } from '../shared/SideBySideDiff';
import { useBrandConfig } from '../../hooks/useBrandConfig';
import { SummarizeButton } from '../shared/SummarizeButton';
/** Extract Jira-style issue keys (e.g. VSM-101, MOB-003) from any text */
function extractJiraKeys(...texts: (string | undefined | null)[]): string[] {
  const keys = new Set<string>();
  for (const text of texts) {
    if (!text) continue;
    const matches = text.match(/\b[A-Z][A-Z0-9]+-\d+\b/g);
    if (matches) matches.forEach(k => keys.add(k));
  }
  return Array.from(keys);
}
/** Small inline badge linking to a Jira issue */
function JiraKeyBadge({
  issueKey,
  jiraBaseUrl
}: {
  issueKey: string;
  jiraBaseUrl: string;
}) {
  return <a href={`${jiraBaseUrl}/${issueKey}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium text-indigo-400 bg-indigo-950/40 border-indigo-900/40 hover:bg-indigo-900/30 hover:text-indigo-300 transition-colors" title={`Ver ${issueKey} en Jira`} data-id="element-4089">
      <TagIcon size={9} data-id="element-4090" />
      {issueKey}
    </a>;
}
// --- Extracted component to avoid useState inside .map() ---
function CommitFileDiff({
  file
}: {
  file: any;
}) {
  const [fileCopied, setFileCopied] = useState(false);
  const [diffFullscreen, setDiffFullscreen] = useState(false);
  const handleFileCopy = () => {
    const text = file.patch || '';
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setFileCopied(true);
        setTimeout(() => setFileCopied(false), 2000);
      }).catch(() => {});
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {}
      document.body.removeChild(ta);
      setFileCopied(true);
      setTimeout(() => setFileCopied(false), 2000);
    }
  };
  return <div className="bg-slate-950/50 border border-slate-800 rounded-xl overflow-hidden" data-id="element-4091">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 bg-slate-900/60" data-id="element-4092">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-300 min-w-0" data-id="element-4093">
          <FileIcon size={14} className="text-slate-500 shrink-0" data-id="element-4094" />
          <span className="truncate" data-id="element-4095">{file.filename}</span>
          <Badge variant={file.status === 'added' ? 'success' : file.status === 'removed' ? 'error' : 'outline'} className="text-[9px] shrink-0" data-id="element-4096">
            {file.status}
          </Badge>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-2" data-id="element-4097">
          <span className="text-xs font-mono text-emerald-400" data-id="element-4098">
            +{file.additions}
          </span>
          <span className="text-xs font-mono text-red-400" data-id="element-4099">
            -{file.deletions}
          </span>
          <button onClick={handleFileCopy} className="p-1 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors ml-1" title="Copiar diff" data-id="element-4100">
            {fileCopied ? <CheckIcon size={13} className="text-emerald-400" data-id="element-4101" /> : <CopyIcon size={13} data-id="element-4102" />}
          </button>
          <button onClick={() => setDiffFullscreen(true)} className="p-1 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors" title="Ver en pantalla completa" data-id="element-4103">
            <Maximize2Icon size={13} data-id="element-4104" />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto bg-[#0d1117] py-1 max-h-[400px] overflow-y-auto" data-id="element-4105">
        <SideBySideDiff patch={file.patch} maxHeight={400} filename={file.filename} fullscreen={diffFullscreen} onFullscreenChange={setDiffFullscreen} data-id="element-4106" />
      </div>
    </div>;
}
type TabType = 'prs' | 'commits' | 'branches' | 'actions';
type VisibilityFilter = 'all' | 'public' | 'private';
type SortOption = 'updated' | 'name' | 'stars';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0
  }
};
// --- Helpers ---
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) return 'hace un momento';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `hace ${diffInMinutes}m`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `hace ${diffInHours}h`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `hace ${diffInDays}d`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `hace ${diffInMonths} meses`;
  return `hace ${Math.floor(diffInMonths / 12)} años`;
}
function getLanguageColor(lang: string | null): string {
  if (!lang) return 'bg-slate-500';
  const colors: Record<string, string> = {
    TypeScript: 'bg-blue-400',
    JavaScript: 'bg-yellow-400',
    Python: 'bg-blue-500',
    Java: 'bg-orange-500',
    HTML: 'bg-orange-600',
    CSS: 'bg-blue-300',
    'C#': 'bg-green-600',
    PHP: 'bg-indigo-400',
    Ruby: 'bg-red-500',
    Go: 'bg-cyan-500',
    Rust: 'bg-orange-400',
    Swift: 'bg-orange-500',
    Kotlin: 'bg-purple-500'
  };
  return colors[lang] || 'bg-slate-400';
}
// --- Code Dropdown (Clone URL + Download ZIP) ---
function CodeDropdown({
  cloneUrl,
  zipUrl
}: {
  cloneUrl: string;
  zipUrl: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(cloneUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="relative z-50" data-id="element-4107">
      <button onClick={() => setOpen(v => !v)} className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-4108">
        <CodeIcon size={16} data-id="element-4109" /> Code <ChevronDownIcon size={14} data-id="element-4110" />
      </button>
      <AnimatePresence data-id="element-4111">
        {open && <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-id="element-4112" />

            <motion.div initial={{
          opacity: 0,
          y: -8,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -8,
          scale: 0.95
        }} transition={{
          duration: 0.15
        }} className="absolute right-0 top-full mt-2 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden" data-id="element-4113">
              <div className="p-4 space-y-3" data-id="element-4114">
                <div data-id="element-4115">
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1.5 block" data-id="element-4116">
                    HTTPS
                  </label>
                  <div className="flex items-center gap-2" data-id="element-4117">
                    <input readOnly value={cloneUrl} className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-slate-300 focus:outline-none select-all" onClick={e => (e.target as HTMLInputElement).select()} data-id="element-4118" />

                    <button onClick={handleCopy} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors text-slate-300" title="Copiar URL" data-id="element-4119">
                      {copied ? <CheckIcon size={14} className="text-emerald-400" data-id="element-4120" /> : <CopyIcon size={14} data-id="element-4121" />}
                    </button>
                  </div>
                </div>
                <div className="border-t border-slate-800 pt-3" data-id="element-4122">
                  <a href={zipUrl} className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-slate-200 hover:bg-slate-800 rounded-lg transition-colors" data-id="element-4123">
                    <DownloadIcon size={16} className="text-slate-400" data-id="element-4124" />
                    Download ZIP
                  </a>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}
// --- Main Component ---
export function GitHubPanel() {
  const {
    activeProject,
    isGlobalView
  } = useProjectContext();
  const {
    brand
  } = useBrandConfig();
  const {
    config,
    repos,
    prs,
    commits,
    branches,
    workflowRuns,
    rateLimit,
    loading,
    reposLoading,
    prsLoading,
    commitsLoading,
    branchesLoading,
    actionsLoading,
    error,
    refresh
  } = useGithubData(activeProject?.key, isGlobalView);
  // Navigation & View State
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('prs');
  const [dismissedError, setDismissedError] = useState(false);
  // Org Overview Filters
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('updated');
  // Branch search (inside repo detail)
  const [branchSearch, setBranchSearch] = useState('');
  const debouncedBranchSearch = useDebounce(branchSearch, 200);
  // PR detail expansion
  const [expandedPR, setExpandedPR] = useState<number | null>(null);
  const [reviewingPR, setReviewingPR] = useState<(typeof prs)[0] | null>(null);
  // Commit detail expansion
  const [expandedCommit, setExpandedCommit] = useState<string | null>(null);
  const [commitFiles, setCommitFiles] = useState<any[]>([]);
  const [commitFilesLoading, setCommitFilesLoading] = useState(false);
  const [commitFilesError, setCommitFilesError] = useState<string | null>(null);
  // README expansion
  const [expandedReadme, setExpandedReadme] = useState<string | null>(null);
  const [readmeFullscreen, setReadmeFullscreen] = useState(false);
  const [readmeContent, setReadmeContent] = useState<string>('');
  const [readmeMeta, setReadmeMeta] = useState<{
    date: string;
    author: string;
    isOutdated: boolean;
  } | null>(null);
  const [readmeLoading, setReadmeLoading] = useState(false);
  const [readmeError, setReadmeError] = useState<string | null>(null);
  // README search
  const [readmeSearch, setReadmeSearch] = useState('');
  const [readmeSearchActive, setReadmeSearchActive] = useState(false);
  const [readmeMatchCount, setReadmeMatchCount] = useState(0);
  const [readmeMatchIndex, setReadmeMatchIndex] = useState(0);
  const readmeContentRef = useRef<HTMLDivElement>(null);
  const readmeSearchInputRef = useRef<HTMLInputElement>(null);
  // Cache both content and meta
  const readmeCacheRef = useRef<Map<string, {
    content: string;
    meta: any;
  }>>(new Map());
  const handleToggleReadme = async (repoName: string) => {
    if (expandedReadme === repoName) {
      setExpandedReadme(null);
      setReadmeFullscreen(false);
      return;
    }
    setExpandedReadme(repoName);
    setReadmeFullscreen(false);
    setReadmeError(null);
    // Check cache
    if (readmeCacheRef.current.has(repoName)) {
      const cached = readmeCacheRef.current.get(repoName)!;
      setReadmeContent(cached.content);
      setReadmeMeta(cached.meta);
      return;
    }
    setReadmeLoading(true);
    setReadmeContent('');
    setReadmeMeta(null);
    try {
      // Fetch content and meta in parallel
      const [result, metaResult] = await Promise.all([githubData.getReadme(repoName), githubData.getReadmeLastCommit(repoName)]);
      if (result) {
        let meta = null;
        if (metaResult) {
          // Check if outdated: if the repo has commits, and the latest commit is significantly newer than the README
          // We'll consider it outdated if the latest commit is more than 30 days newer than the README
          // Or simpler: just get the latest commit from our scopedCommits if available
          let isOutdated = false;
          const repoCommits = commits.filter(c => c.repoName === repoName);
          if (repoCommits.length > 0) {
            const latestCommitDate = new Date(repoCommits[0].author.date).getTime();
            const readmeDate = new Date(metaResult.date).getTime();
            // If README is more than 30 days older than the latest commit
            isOutdated = latestCommitDate - readmeDate > 30 * 24 * 60 * 60 * 1000;
          }
          meta = {
            date: metaResult.date,
            author: metaResult.author,
            isOutdated
          };
          setReadmeMeta(meta);
        }
        readmeCacheRef.current.set(repoName, {
          content: result.content,
          meta
        });
        setReadmeContent(result.content);
      } else {
        setReadmeError('Este repositorio no tiene README.');
      }
    } catch (err: any) {
      setReadmeError(err.message || 'Error al cargar el README');
    } finally {
      setReadmeLoading(false);
    }
  };
  // README search highlight effect
  useEffect(() => {
    const container = readmeContentRef.current;
    if (!container) return;
    // Clear previous highlights
    container.querySelectorAll('mark[data-readme-highlight]').forEach(mark => {
      const parent = mark.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
        parent.normalize();
      }
    });
    if (!readmeSearch.trim()) {
      setReadmeMatchCount(0);
      setReadmeMatchIndex(0);
      return;
    }
    const searchTerm = readmeSearch.trim().toLowerCase();
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    const textNodes: Text[] = [];
    let node: Text | null;
    while (node = walker.nextNode() as Text | null) {
      if (node.textContent && node.textContent.toLowerCase().includes(searchTerm)) {
        textNodes.push(node);
      }
    }
    let totalMatches = 0;
    textNodes.forEach(textNode => {
      const text = textNode.textContent || '';
      const parts: (string | {
        match: string;
      })[] = [];
      let lastIndex = 0;
      const lowerText = text.toLowerCase();
      let idx = lowerText.indexOf(searchTerm, lastIndex);
      while (idx !== -1) {
        if (idx > lastIndex) parts.push(text.slice(lastIndex, idx));
        parts.push({
          match: text.slice(idx, idx + searchTerm.length)
        });
        totalMatches++;
        lastIndex = idx + searchTerm.length;
        idx = lowerText.indexOf(searchTerm, lastIndex);
      }
      if (lastIndex < text.length) parts.push(text.slice(lastIndex));
      if (parts.length > 1) {
        const fragment = document.createDocumentFragment();
        parts.forEach(part => {
          if (typeof part === 'string') {
            fragment.appendChild(document.createTextNode(part));
          } else {
            const mark = document.createElement('mark');
            mark.setAttribute('data-readme-highlight', 'true');
            mark.textContent = part.match;
            mark.style.backgroundColor = 'rgba(99, 102, 241, 0.3)';
            mark.style.color = '#e0e7ff';
            mark.style.borderRadius = '2px';
            mark.style.padding = '1px 0';
            fragment.appendChild(mark);
          }
        });
        textNode.parentNode?.replaceChild(fragment, textNode);
      }
    });
    setReadmeMatchCount(totalMatches);
    if (totalMatches > 0) {
      setReadmeMatchIndex(prev => Math.min(prev, totalMatches - 1));
    } else {
      setReadmeMatchIndex(0);
    }
  }, [readmeSearch, readmeContent, expandedReadme]);
  // Scroll to active match
  useEffect(() => {
    const container = readmeContentRef.current;
    if (!container || readmeMatchCount === 0) return;
    const marks = container.querySelectorAll('mark[data-readme-highlight]');
    marks.forEach((m, i) => {
      const el = m as HTMLElement;
      if (i === readmeMatchIndex) {
        el.style.backgroundColor = 'rgba(99, 102, 241, 0.7)';
        el.style.boxShadow = '0 0 0 2px rgba(99, 102, 241, 0.4)';
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      } else {
        el.style.backgroundColor = 'rgba(99, 102, 241, 0.3)';
        el.style.boxShadow = 'none';
      }
    });
  }, [readmeMatchIndex, readmeMatchCount]);
  // Keyboard shortcut for README search
  useEffect(() => {
    if (!expandedReadme) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setReadmeSearchActive(true);
        setTimeout(() => readmeSearchInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && readmeSearchActive) {
        setReadmeSearchActive(false);
        setReadmeSearch('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedReadme, readmeSearchActive]);
  // Reset search when closing README
  useEffect(() => {
    if (!expandedReadme) {
      setReadmeSearch('');
      setReadmeSearchActive(false);
      setReadmeMatchCount(0);
      setReadmeMatchIndex(0);
    }
  }, [expandedReadme]);
  // Force fetch on mount
  useEffect(() => {
    refresh();
  }, [refresh]);
  // Reset dismissed error when a new error occurs
  useEffect(() => {
    if (error) setDismissedError(false);
  }, [error]);
  // Reset tab, branch search, and expanded PR when changing repo
  useEffect(() => {
    setActiveTab('prs');
    setBranchSearch('');
    setExpandedPR(null);
  }, [selectedRepo]);
  // --- Data Processing ---
  const filteredRepos = useMemo(() => {
    return repos.filter(r => {
      if (visibilityFilter === 'private' && !r.private) return false;
      if (visibilityFilter === 'public' && r.private) return false;
      if (debouncedSearch && !r.name.toLowerCase().includes(debouncedSearch.toLowerCase())) return false;
      return true;
    }).sort((a, b) => {
      if (sortBy === 'updated') return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'stars') return b.stargazers_count - a.stargazers_count;
      return 0;
    });
  }, [repos, visibilityFilter, debouncedSearch, sortBy]);
  const activeRepoObj = useMemo(() => repos.find(r => r.name === selectedRepo), [repos, selectedRepo]);
  const scopedPRs = useMemo(() => prs.filter(p => p.repoName === selectedRepo), [prs, selectedRepo]);
  const scopedCommits = useMemo(() => commits.filter(c => c.repoName === selectedRepo), [commits, selectedRepo]);
  const scopedBranches = useMemo(() => branches.filter(b => b.repoName === selectedRepo), [branches, selectedRepo]);
  const scopedActions = useMemo(() => workflowRuns.filter(r => r.repoName === selectedRepo), [workflowRuns, selectedRepo]);
  const topContributors = useMemo(() => {
    const map = new Map<string, {
      login: string;
      avatar: string;
      count: number;
    }>();
    scopedCommits.forEach(c => {
      if (!map.has(c.author.login)) {
        map.set(c.author.login, {
          login: c.author.login,
          avatar: c.author.avatar_url,
          count: 0
        });
      }
      map.get(c.author.login)!.count++;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [scopedCommits]);
  // --- Render Helpers ---
  const renderRateLimit = () => {
    if (!rateLimit) return null;
    const percentage = rateLimit.remaining / rateLimit.limit * 100;
    const radius = 14;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - percentage / 100 * circumference;
    const isLow = rateLimit.remaining < 1000;
    return <div className="flex items-center gap-3 bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-full pl-3 pr-4 py-1.5 hidden sm:flex" data-id="element-4125">
        <div className="relative w-8 h-8 flex items-center justify-center" data-id="element-4126">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36" data-id="element-4127">
            <circle cx="18" cy="18" r={radius} fill="none" className="stroke-slate-800" strokeWidth="3" data-id="element-4128" />

            <circle cx="18" cy="18" r={radius} fill="none" className={isLow ? 'stroke-red-500' : 'stroke-emerald-500'} strokeWidth="3" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" style={{
            transition: 'stroke-dashoffset 1s ease-in-out'
          }} data-id="element-4129" />
          </svg>
          <ActivityIcon size={10} className={`absolute ${isLow ? 'text-red-500' : 'text-emerald-500'}`} data-id="element-4130" />
        </div>
        <div data-id="element-4131">
          <p className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-0.5" data-id="element-4132">
            API Limit
          </p>
          <p className="text-xs font-mono font-medium text-slate-300" data-id="element-4133">
            {rateLimit.remaining}{' '}
            <span className="text-slate-600" data-id="element-4134">/ {rateLimit.limit}</span>
          </p>
        </div>
      </div>;
  };
  const renderKPIs = (dataPRs: typeof prs, dataCommits: typeof commits, dataBranches: typeof branches, dataActions: typeof workflowRuns) => <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-4135">
      {[{
      label: 'Open PRs',
      count: dataPRs.filter(p => p.state === 'open').length,
      icon: GitPullRequestIcon,
      color: 'text-emerald-400',
      bg: 'bg-emerald-400/10'
    }, {
      label: 'Recent Commits',
      count: dataCommits.length,
      icon: GitCommitIcon,
      color: 'text-blue-400',
      bg: 'bg-blue-400/10'
    }, {
      label: 'Active Branches',
      count: dataBranches.length,
      icon: GitBranchIcon,
      color: 'text-purple-400',
      bg: 'bg-purple-400/10'
    }, {
      label: 'Failed Actions',
      count: dataActions.filter(r => r.conclusion === 'failure').length,
      icon: XCircleIcon,
      color: 'text-red-400',
      bg: 'bg-red-400/10'
    }].map((stat, i) => <motion.div key={i} initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: i * 0.1
    }} className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-4 flex items-center gap-4 shadow-sm" data-id="element-4136">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stat.bg} ${stat.color}`} data-id="element-4137">
            <stat.icon size={20} data-id="element-4138" />
          </div>
          <div data-id="element-4139">
            <p className="text-2xl font-bold text-slate-100" data-id="element-4140">{stat.count}</p>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-4141">
              {stat.label}
            </p>
          </div>
        </motion.div>)}
    </div>;
  // --- Level 1: Org Overview ---
  const renderOrgOverview = () => <motion.div key="org-overview" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.2
  }} className="space-y-6" data-id="element-4142">
      {renderKPIs(prs, commits, branches, workflowRuns)}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 p-4 rounded-2xl" data-id="element-4143">
        <div className="relative w-full md:max-w-md" data-id="element-4144">
          <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-4145" />

          <input type="text" placeholder="Buscar repositorios..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2 pl-10 pr-4 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" data-id="element-4146" />
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto overflow-x-auto pb-1 md:pb-0 scrollbar-hide" data-id="element-4147">
          <div className="flex bg-slate-950 rounded-lg p-1 border border-slate-800" data-id="element-4148">
            {(['all', 'public', 'private'] as VisibilityFilter[]).map(filter => <button key={filter} onClick={() => setVisibilityFilter(filter)} className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${visibilityFilter === filter ? 'bg-slate-800 text-slate-200' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-4149">
                  {filter}
                </button>)}
          </div>
          <div className="relative group" data-id="element-4150">
            <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="appearance-none bg-slate-950 border border-slate-800 rounded-lg py-1.5 pl-3 pr-8 text-xs font-medium text-slate-300 focus:outline-none focus:border-indigo-500 cursor-pointer" data-id="element-4151">
              <option value="updated" data-id="element-4152">Last pushed</option>
              <option value="name" data-id="element-4153">Name</option>
              <option value="stars" data-id="element-4154">Stars</option>
            </select>
            <ChevronDownIcon size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-4155" />
          </div>
        </div>
      </div>

      {/* Repo Grid */}
      {reposLoading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4156">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5 h-36 relative overflow-hidden" data-id="element-4157">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-slate-800/20 to-transparent" data-id="element-4158" />
            </div>)}
        </div> : filteredRepos.length === 0 ? <EmptyState icon={SearchIcon} message="No se encontraron repositorios con esos filtros." data-id="element-4159" /> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4160">
          {filteredRepos.map(repo => <Fragment key={repo.id} data-id="element-4161">
              <motion.div variants={itemVariants} className={`cursor-pointer bg-slate-900/40 backdrop-blur-sm border rounded-2xl p-5 hover:bg-slate-800/60 transition-all group shadow-sm hover:shadow-md flex flex-col h-full ${expandedReadme === repo.name ? 'border-indigo-500/40 ring-1 ring-indigo-500/20' : 'border-slate-800/50 hover:border-indigo-500/30'}`} data-id="element-4162">
                <div onClick={() => setSelectedRepo(repo.name)} className="flex-1" data-id="element-4163">
                  <div className="flex justify-between items-start mb-2 gap-2" data-id="element-4164">
                    <h3 className="text-base font-bold text-slate-200 group-hover:text-indigo-400 transition-colors truncate" data-id="element-4165">
                      {repo.name}
                    </h3>
                    <Badge variant="outline" className="shrink-0 text-[10px] capitalize" data-id="element-4166">
                      {repo.private ? 'Private' : 'Public'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4 flex-1" data-id="element-4167">
                    {repo.description || <span className="italic opacity-50" data-id="element-4168">Sin descripción</span>}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500 mt-auto pt-4 border-t border-slate-800/50" data-id="element-4169">
                  {repo.language && <span className="flex items-center gap-1.5 font-medium" data-id="element-4170">
                      <span className={`w-2 h-2 rounded-full ${getLanguageColor(repo.language)}`} data-id="element-4171" />

                      {repo.language}
                    </span>}
                  <span className="flex items-center gap-1" data-id="element-4172">
                    <StarIcon size={12} data-id="element-4173" /> {repo.stargazers_count}
                  </span>
                  <span className="flex items-center gap-1" data-id="element-4174">
                    <CircleDotIcon size={12} data-id="element-4175" /> {repo.open_issues_count}
                  </span>
                  <span className="ml-auto flex items-center gap-3" data-id="element-4176">
                    <button onClick={e => {
                e.stopPropagation();
                handleToggleReadme(repo.name);
              }} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${expandedReadme === repo.name ? 'text-indigo-400 bg-indigo-500/10 border border-indigo-500/20' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800 border border-transparent'}`} data-id="element-4177">
                      <FileTextIcon size={12} data-id="element-4178" />
                      README
                    </button>
                    <span className="text-slate-500 truncate" data-id="element-4179">
                      {getRelativeTime(repo.updated_at)}
                    </span>
                  </span>
                </div>
              </motion.div>
            </Fragment>)}
        </motion.div>}
    </motion.div>;
  // --- Level 2: Repo Detail ---
  const renderRepoDetail = () => {
    if (!activeRepoObj) return null;
    const tabs = [{
      id: 'prs',
      label: 'Pull Requests',
      icon: GitPullRequestIcon,
      count: scopedPRs.length,
      loading: prsLoading
    }, {
      id: 'commits',
      label: 'Commits',
      icon: GitCommitIcon,
      count: scopedCommits.length,
      loading: commitsLoading
    }, {
      id: 'branches',
      label: 'Branches',
      icon: GitBranchIcon,
      count: scopedBranches.length,
      loading: branchesLoading
    }, {
      id: 'actions',
      label: 'Actions',
      icon: PlayCircleIcon,
      count: scopedActions.length,
      loading: actionsLoading
    }];
    return <motion.div key="repo-detail" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.2
    }} className="space-y-6" data-id="element-4180">
        {/* Breadcrumb */}
        <button onClick={() => setSelectedRepo(null)} className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-indigo-400 transition-colors w-fit" data-id="element-4181">
          <ArrowLeftIcon size={16} data-id="element-4182" /> Volver a {config.org}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-4183">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6" data-id="element-4184">
            {/* Repo Header */}
            <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 shadow-sm relative z-30 overflow-visible" data-id="element-4185">
              <div className="flex items-start justify-between gap-4 mb-4" data-id="element-4186">
                <div data-id="element-4187">
                  <div className="flex items-center gap-3 mb-2" data-id="element-4188">
                    <h2 className="text-2xl font-bold text-slate-100" data-id="element-4189">
                      {activeRepoObj.name}
                    </h2>
                    <Badge variant="outline" className="capitalize text-xs" data-id="element-4190">
                      {activeRepoObj.private ? 'Private' : 'Public'}
                    </Badge>
                  </div>
                  <p className="text-slate-400 text-sm max-w-2xl" data-id="element-4191">
                    {activeRepoObj.description || 'Sin descripción proporcionada.'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0" data-id="element-4192">
                  <a href={activeRepoObj.html_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors" title="Abrir en GitHub" data-id="element-4193">
                    <ExternalLinkIcon size={18} data-id="element-4194" />
                  </a>
                  <CodeDropdown cloneUrl={`https://github.com/${config.org}/${activeRepoObj.name}.git`} zipUrl={`https://github.com/${config.org}/${activeRepoObj.name}/archive/refs/heads/${activeRepoObj.default_branch}.zip`} data-id="element-4195" />
                </div>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-400 border-t border-slate-800/50 pt-4 mt-4" data-id="element-4196">
                <span className="flex items-center gap-2" data-id="element-4197">
                  <GitBranchIcon size={16} className="text-slate-500" data-id="element-4198" />{' '}
                  {scopedBranches.length} Branches
                </span>
                <span className="flex items-center gap-2" data-id="element-4199">
                  <GitPullRequestIcon size={16} className="text-slate-500" data-id="element-4200" />{' '}
                  {scopedPRs.length} PRs
                </span>
                <span className="flex items-center gap-2" data-id="element-4201">
                  <GitCommitIcon size={16} className="text-slate-500" data-id="element-4202" />{' '}
                  {scopedCommits.length} Commits
                </span>
                <span className="flex items-center gap-2" data-id="element-4203">
                  <PlayCircleIcon size={16} className="text-slate-500" data-id="element-4204" />{' '}
                  {scopedActions.length} Actions
                </span>
              </div>
            </div>

            {renderKPIs(scopedPRs, scopedCommits, scopedBranches, scopedActions)}

            {/* Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-4205">
              <div className="flex p-1.5 bg-slate-900/50 backdrop-blur-md rounded-2xl border border-slate-800/50 w-max" data-id="element-4206">
                {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)} className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap z-10 ${isActive ? 'text-indigo-300' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-4207">
                      {isActive && <motion.div layoutId="activeTabPill" className="absolute inset-0 bg-indigo-500/10 border border-indigo-500/20 rounded-xl -z-10" transition={{
                    type: 'spring',
                    bounce: 0.2,
                    duration: 0.6
                  }} data-id="element-4208" />}
                      <Icon size={16} className={isActive ? 'text-indigo-400' : 'opacity-70'} data-id="element-4209" />

                      {tab.label}
                      <span className={`ml-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold transition-colors ${isActive ? 'bg-indigo-500/20 text-indigo-300' : 'bg-slate-800 text-slate-500'}`} data-id="element-4210">
                        {tab.loading ? '...' : tab.count}
                      </span>
                    </button>;
              })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]" data-id="element-4211">
              {activeTab === 'prs' && renderPRs(scopedPRs)}
              {activeTab === 'commits' && renderCommits(scopedCommits)}
              {activeTab === 'branches' && renderBranches(scopedBranches)}
              {activeTab === 'actions' && renderActions(scopedActions)}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4" data-id="element-4212">
            <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-5 shadow-sm" data-id="element-4213">
              <h3 className="text-sm font-bold text-slate-200 mb-4 flex items-center gap-2" data-id="element-4214">
                <CodeIcon size={16} className="text-indigo-400" data-id="element-4215" /> About
              </h3>
              <div className="space-y-4 text-sm" data-id="element-4216">
                {activeRepoObj.language && <div className="flex items-center justify-between" data-id="element-4217">
                    <span className="text-slate-400" data-id="element-4218">Language</span>
                    <span className="flex items-center gap-1.5 font-medium text-slate-200" data-id="element-4219">
                      <span className={`w-2.5 h-2.5 rounded-full ${getLanguageColor(activeRepoObj.language)}`} data-id="element-4220" />

                      {activeRepoObj.language}
                    </span>
                  </div>}
                <div className="flex items-center justify-between" data-id="element-4221">
                  <span className="text-slate-400" data-id="element-4222">Stars</span>
                  <span className="font-medium text-slate-200 flex items-center gap-1" data-id="element-4223">
                    <StarIcon size={14} className="text-yellow-500" data-id="element-4224" />{' '}
                    {activeRepoObj.stargazers_count}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-4225">
                  <span className="text-slate-400" data-id="element-4226">Default Branch</span>
                  <Badge variant="outline" className="font-mono text-[10px]" data-id="element-4227">
                    {activeRepoObj.default_branch}
                  </Badge>
                </div>
                <div className="flex items-center justify-between" data-id="element-4228">
                  <span className="text-slate-400" data-id="element-4229">Last Updated</span>
                  <span className="font-medium text-slate-200 flex items-center gap-1" data-id="element-4230">
                    <CalendarIcon size={14} className="text-slate-500" data-id="element-4231" />{' '}
                    {getRelativeTime(activeRepoObj.updated_at)}
                  </span>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-800/50" data-id="element-4232">
                <button onClick={() => handleToggleReadme(activeRepoObj.name)} className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${expandedReadme === activeRepoObj.name ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800 border border-slate-700/50 hover:border-slate-600'}`} data-id="element-4233">
                  <FileTextIcon size={16} className={expandedReadme === activeRepoObj.name ? 'text-indigo-400' : 'text-slate-400'} data-id="element-4234" />

                  {expandedReadme === activeRepoObj.name ? 'Cerrar README' : 'Ver README'}
                </button>
              </div>
            </div>

            {topContributors.length > 0 && <div className="bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-5 shadow-sm" data-id="element-4235">
                <h3 className="text-sm font-bold text-slate-200 mb-4 flex items-center gap-2" data-id="element-4236">
                  <UsersIcon size={16} className="text-indigo-400" data-id="element-4237" /> Top
                  Contributors
                </h3>
                <div className="space-y-3" data-id="element-4238">
                  {topContributors.map(c => <div key={c.login} className="flex items-center justify-between" data-id="element-4239">
                      <div className="flex items-center gap-2" data-id="element-4240">
                        <img src={c.avatar} alt={c.login} className="w-6 h-6 rounded-full ring-1 ring-slate-800" data-id="element-4241" />

                        <span className="text-sm text-slate-300" data-id="element-4242">
                          {c.login}
                        </span>
                      </div>
                      <span className="text-xs font-medium text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded-full" data-id="element-4243">
                        {c.count} commits
                      </span>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </motion.div>;
  };
  // --- Tab Renderers (Reused but scoped) ---
  const renderPRs = (data: typeof prs) => {
    if (prsLoading) return <LoadingState data-id="element-4244" />;
    if (data.length === 0) return <EmptyState icon={GitPullRequestIcon} message="No hay Pull Requests en este repositorio." data-id="element-4245" />;
    return <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-3" data-id="element-4246">
        {data.map(pr => {
        let stateColor = 'border-l-emerald-500';
        let iconColor = 'text-emerald-500';
        let stateBg = 'bg-emerald-500/10';
        let stateLabel = 'Open';
        if (pr.merged) {
          stateColor = 'border-l-purple-500';
          iconColor = 'text-purple-500';
          stateBg = 'bg-purple-500/10';
          stateLabel = 'Merged';
        } else if (pr.state === 'closed') {
          stateColor = 'border-l-red-500';
          iconColor = 'text-red-500';
          stateBg = 'bg-red-500/10';
          stateLabel = 'Closed';
        } else if (pr.draft) {
          stateColor = 'border-l-slate-500';
          iconColor = 'text-slate-500';
          stateBg = 'bg-slate-500/10';
          stateLabel = 'Draft';
        }
        const isExpanded = expandedPR === pr.id;
        const totalChanges = pr.additions + pr.deletions;
        const addPercent = totalChanges > 0 ? pr.additions / totalChanges * 100 : 50;
        return <motion.div variants={itemVariants} key={pr.id} className={`bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 border-l-4 ${stateColor} rounded-xl overflow-hidden transition-all shadow-sm ${isExpanded ? 'shadow-lg shadow-indigo-500/5 border-slate-700/50' : 'hover:shadow-md'}`} data-id="element-4247">
              {/* Clickable header */}
              <div onClick={() => setExpandedPR(isExpanded ? null : pr.id)} className="p-5 cursor-pointer group" data-id="element-4248">
                <div className="flex items-start gap-4" data-id="element-4249">
                  <div className="mt-0.5 bg-slate-950 p-2 rounded-lg border border-slate-800" data-id="element-4250">
                    {pr.merged ? <GitMergeIcon size={18} className={iconColor} data-id="element-4251" /> : <GitPullRequestIcon size={18} className={iconColor} data-id="element-4252" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4253">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-4254">
                      <h3 className={`text-base font-semibold transition-colors ${isExpanded ? 'text-indigo-300' : 'text-slate-200 group-hover:text-indigo-300'}`} data-id="element-4255">
                        {pr.title}
                      </h3>
                      {pr.labels.map(l => <span key={l.name} className="px-2 py-0.5 rounded-md text-[10px] font-medium border border-slate-700/50 bg-slate-800/30 text-slate-300 whitespace-nowrap" data-id="element-4256">
                          {l.name}
                        </span>)}
                      {extractJiraKeys(pr.title, pr.head?.ref, pr.body).map(key => <JiraKeyBadge key={key} issueKey={key} jiraBaseUrl={brand.jiraBaseUrl} data-id="element-4257" />)}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-4258">
                      <span className="font-mono text-slate-400 bg-slate-950 px-1.5 py-0.5 rounded border border-slate-800" data-id="element-4259">
                        #{pr.number}
                      </span>
                      <span className="flex items-center gap-1.5" data-id="element-4260">
                        <img src={pr.user.avatar_url} alt="" className="w-4 h-4 rounded-full ring-1 ring-slate-800" data-id="element-4261" />

                        {pr.user.login}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-4262">
                        <ClockIcon size={12} data-id="element-4263" />{' '}
                        {new Date(pr.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-2 font-mono" data-id="element-4264">
                        <span className="text-emerald-400/80" data-id="element-4265">
                          +{pr.additions}
                        </span>
                        <span className="text-red-400/80" data-id="element-4266">-{pr.deletions}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1" data-id="element-4267">
                    <motion.div animate={{
                  rotate: isExpanded ? 90 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-4268">
                      <ChevronRightIcon size={16} className="text-slate-500" data-id="element-4269" />
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Expandable detail panel */}
              <AnimatePresence data-id="element-4270">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-4271">
                    <div className="px-5 pb-5 border-t border-slate-800/50" data-id="element-4272">
                      <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4273">
                        {/* Left column: Branch flow + State */}
                        <div className="space-y-4" data-id="element-4274">
                          {/* State */}
                          <div className="flex items-center gap-3" data-id="element-4275">
                            <div className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 ${stateBg} ${iconColor}`} data-id="element-4276">
                              {pr.merged ? <GitMergeIcon size={14} data-id="element-4277" /> : <GitPullRequestIcon size={14} data-id="element-4278" />}
                              {stateLabel}
                            </div>
                            {pr.draft && <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-1 rounded-md font-medium" data-id="element-4279">
                                DRAFT
                              </span>}
                          </div>

                          {/* Branch flow */}
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-4280">
                            <span className="flex items-center gap-1.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2.5 py-1 rounded-lg text-xs font-mono" data-id="element-4281">
                              <GitBranchIcon size={12} data-id="element-4282" />
                              {pr.head.ref}
                            </span>
                            <ArrowRightIcon size={14} className="text-slate-600" data-id="element-4283" />

                            <span className="flex items-center gap-1.5 bg-slate-800 text-slate-300 border border-slate-700 px-2.5 py-1 rounded-lg text-xs font-mono" data-id="element-4284">
                              <GitBranchIcon size={12} data-id="element-4285" />
                              {pr.base.ref}
                            </span>
                          </div>

                          {/* Jira key */}
                          {pr.jiraKey && <div className="flex items-center gap-2" data-id="element-4286">
                              <TagIcon size={14} className="text-slate-500" data-id="element-4287" />
                              <span className="text-xs font-medium text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded-md" data-id="element-4288">
                                {pr.jiraKey}
                              </span>
                            </div>}

                          {/* Dates */}
                          <div className="space-y-2 text-xs text-slate-500" data-id="element-4289">
                            <div className="flex items-center gap-2" data-id="element-4290">
                              <ClockIcon size={12} data-id="element-4291" />
                              <span data-id="element-4292">
                                Creado:{' '}
                                {new Date(pr.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-4293">
                              <ClockIcon size={12} data-id="element-4294" />
                              <span data-id="element-4295">
                                Actualizado: {getRelativeTime(pr.updated_at)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right column: Diff stats */}
                        <div className="space-y-4" data-id="element-4296">
                          {/* Changed files */}
                          <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-4 space-y-3" data-id="element-4297">
                            <div className="flex items-center justify-between" data-id="element-4298">
                              <span className="text-xs font-medium text-slate-400 flex items-center gap-1.5" data-id="element-4299">
                                <FileIcon size={14} data-id="element-4300" /> Archivos cambiados
                              </span>
                              <span className="text-sm font-bold text-slate-200" data-id="element-4301">
                                {pr.changed_files}
                              </span>
                            </div>

                            {/* Additions / Deletions */}
                            <div className="space-y-2" data-id="element-4302">
                              <div className="flex items-center justify-between text-xs" data-id="element-4303">
                                <span className="text-emerald-400 font-mono font-medium" data-id="element-4304">
                                  +{pr.additions} additions
                                </span>
                                <span className="text-red-400 font-mono font-medium" data-id="element-4305">
                                  -{pr.deletions} deletions
                                </span>
                              </div>
                              {/* Visual diff bar */}
                              <div className="h-2 rounded-full overflow-hidden flex bg-slate-800" data-id="element-4306">
                                <div className="bg-emerald-500 transition-all duration-500" style={{
                            width: `${addPercent}%`
                          }} data-id="element-4307" />

                                <div className="bg-red-500 transition-all duration-500" style={{
                            width: `${100 - addPercent}%`
                          }} data-id="element-4308" />
                              </div>
                              <p className="text-[10px] text-slate-600 text-center" data-id="element-4309">
                                {totalChanges} cambios totales
                              </p>
                            </div>
                          </div>

                          {/* Author card */}
                          <div className="flex items-center gap-3 bg-slate-950/50 border border-slate-800 rounded-xl p-3" data-id="element-4310">
                            <img src={pr.user.avatar_url} alt={pr.user.login} className="w-8 h-8 rounded-full ring-2 ring-slate-700" data-id="element-4311" />

                            <div data-id="element-4312">
                              <p className="text-sm font-medium text-slate-200" data-id="element-4313">
                                {pr.user.login}
                              </p>
                              <p className="text-[10px] text-slate-500" data-id="element-4314">
                                Autor del PR
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action bar */}
                      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-800/50" data-id="element-4315">
                        <button onClick={e => {
                    e.stopPropagation();
                    setReviewingPR(pr);
                  }} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-4316">
                          <FileIcon size={14} data-id="element-4317" /> Review PR
                        </button>
                        <a href={pr.html_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium rounded-lg transition-colors" data-id="element-4318">
                          <ExternalLinkIcon size={14} data-id="element-4319" /> Ver en GitHub
                        </a>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>;
      })}
      </motion.div>;
  };
  const handleExpandCommit = async (commit: (typeof commits)[0]) => {
    if (expandedCommit === commit.sha) {
      setExpandedCommit(null);
      setCommitFiles([]);
      setCommitFilesError(null);
      return;
    }
    setExpandedCommit(commit.sha);
    setCommitFiles([]);
    setCommitFilesLoading(true);
    setCommitFilesError(null);
    try {
      const files = await githubData.getCommitFiles(commit.repoName, commit.sha);
      setCommitFiles(files);
    } catch (err: any) {
      setCommitFilesError(err.message);
    } finally {
      setCommitFilesLoading(false);
    }
  };
  const renderCommits = (data: typeof commits) => {
    if (commitsLoading) return <LoadingState data-id="element-4320" />;
    if (data.length === 0) return <EmptyState icon={GitCommitIcon} message="No hay Commits recientes en este repositorio." data-id="element-4321" />;
    return <motion.div variants={containerVariants} initial="hidden" animate="show" className="relative border-l border-slate-800/60 ml-4 py-2 space-y-8" data-id="element-4322">
        {data.map(commit => {
        const isExpanded = expandedCommit === commit.sha;
        return <motion.div variants={itemVariants} key={commit.sha} className="relative pl-8 group" data-id="element-4323">
              <div className={`absolute -left-3 top-1.5 w-6 h-6 bg-slate-950 border-2 rounded-full flex items-center justify-center transition-colors z-10 ${isExpanded ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-700 group-hover:border-indigo-500 group-hover:bg-indigo-500/10'}`} data-id="element-4324">
                <GitCommitIcon size={12} className={isExpanded ? 'text-indigo-400' : 'text-slate-400 group-hover:text-indigo-400'} data-id="element-4325" />
              </div>
              <div className={`bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-xl overflow-hidden transition-all shadow-sm ${isExpanded ? 'shadow-lg shadow-indigo-500/5 border-slate-700/50' : 'hover:shadow-md hover:bg-slate-800/40'}`} data-id="element-4326">
                {/* Clickable header */}
                <div onClick={() => handleExpandCommit(commit)} className="p-4 cursor-pointer" data-id="element-4327">
                  <div className="flex items-start justify-between gap-4" data-id="element-4328">
                    <div className="flex-1 min-w-0" data-id="element-4329">
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-id="element-4330">
                        <h3 className={`text-sm font-medium transition-colors truncate ${isExpanded ? 'text-indigo-300' : 'text-slate-200 group-hover:text-indigo-300'}`} data-id="element-4331">
                          {commit.message}
                        </h3>
                        {extractJiraKeys(commit.commit?.message || commit.message).map(key => <JiraKeyBadge key={key} issueKey={key} jiraBaseUrl={brand.jiraBaseUrl} data-id="element-4332" />)}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-4333">
                        <span className="font-mono text-indigo-400/80 bg-indigo-500/10 px-1.5 py-0.5 rounded border border-indigo-500/20" data-id="element-4334">
                          {commit.sha.slice(0, 7)}
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-4335">
                          {commit.author.avatar_url && <img src={commit.author.avatar_url} alt="" className="w-4 h-4 rounded-full ring-1 ring-slate-800" data-id="element-4336" />}
                          {commit.author.login}
                        </span>
                        <span data-id="element-4337">
                          {new Date(commit.author.date).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1" data-id="element-4338">
                      <motion.div animate={{
                    rotate: isExpanded ? 90 : 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-4339">
                        <ChevronRightIcon size={16} className="text-slate-500" data-id="element-4340" />
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Expandable diff panel */}
                <AnimatePresence data-id="element-4341">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-4342">
                      <div className="px-4 pb-4 border-t border-slate-800/50" data-id="element-4343">
                        <div className="pt-4 space-y-4" data-id="element-4344">
                          {commitFilesLoading ? <div className="flex items-center justify-center py-8 text-slate-500 gap-2" data-id="element-4345">
                              <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-4346" />
                              <span className="text-sm" data-id="element-4347">Cargando diff...</span>
                            </div> : commitFilesError ? <div className="p-3 bg-red-950/40 border border-red-900/50 rounded-lg text-red-400 text-sm flex items-center gap-2" data-id="element-4348">
                              <AlertCircleIcon size={16} className="shrink-0" data-id="element-4349" />
                              {commitFilesError}
                            </div> : commitFiles.length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-id="element-4350">
                              No hay archivos cambiados en este commit.
                            </p> : <>
                              {/* Summary bar */}
                              <div className="flex items-center gap-4 text-xs text-slate-400 bg-slate-950/50 border border-slate-800 rounded-lg px-3 py-2" data-id="element-4351">
                                <span className="flex items-center gap-1.5" data-id="element-4352">
                                  <FileIcon size={12} data-id="element-4353" />
                                  {commitFiles.length} archivo
                                  {commitFiles.length !== 1 ? 's' : ''} cambiado
                                  {commitFiles.length !== 1 ? 's' : ''}
                                </span>
                                <span className="text-emerald-400 font-mono" data-id="element-4354">
                                  +
                                  {commitFiles.reduce((s, f) => s + f.additions, 0)}
                                </span>
                                <span className="text-red-400 font-mono" data-id="element-4355">
                                  -
                                  {commitFiles.reduce((s, f) => s + f.deletions, 0)}
                                </span>
                              </div>

                              {/* File diffs */}
                              {commitFiles.map(file => <CommitFileDiff key={file.sha + file.filename} file={file} data-id="element-4356" />)}
                            </>}

                          {/* Action bar */}
                          <div className="flex items-center gap-3 pt-2" data-id="element-4357">
                            <a href={commit.html_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium rounded-lg transition-colors" data-id="element-4358">
                              <ExternalLinkIcon size={14} data-id="element-4359" /> Ver en GitHub
                            </a>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </motion.div>;
  };
  const renderBranches = (data: typeof branches) => {
    if (branchesLoading) return <LoadingState data-id="element-4360" />;
    if (data.length === 0) return <EmptyState icon={GitBranchIcon} message="No hay Branches en este repositorio." data-id="element-4361" />;
    // Filter branches by search
    const filteredBranches = debouncedBranchSearch ? data.filter(b => b.name.toLowerCase().includes(debouncedBranchSearch.toLowerCase())) : data;
    // Find last commit for each branch from scopedCommits
    const branchLastCommit = new Map<string, (typeof commits)[0]>();
    scopedCommits.forEach(c => {
      // Match commits to branches by checking if any branch points to this commit
      data.forEach(b => {
        if (b.commit.sha.startsWith(c.sha.slice(0, 7)) || c.sha.startsWith(b.commit.sha.slice(0, 7))) {
          if (!branchLastCommit.has(b.name)) {
            branchLastCommit.set(b.name, c);
          }
        }
      });
    });
    return <div className="space-y-4" data-id="element-4362">
        {/* Branch search */}
        <div className="relative max-w-sm" data-id="element-4363">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-4364" />

          <input type="text" placeholder="Buscar branches..." value={branchSearch} onChange={e => setBranchSearch(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2 pl-9 pr-4 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" data-id="element-4365" />

          {branchSearch && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 font-medium" data-id="element-4366">
              {filteredBranches.length}/{data.length}
            </span>}
        </div>

        {filteredBranches.length === 0 ? <EmptyState icon={SearchIcon} message={`No se encontraron branches con "${branchSearch}"`} data-id="element-4367" /> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="columns-1 md:columns-2 gap-4 space-y-4" data-id="element-4368">
            {filteredBranches.map(branch => {
          const lastCommit = branchLastCommit.get(branch.name);
          return <motion.a variants={itemVariants} key={branch.name} href={branch.commit.url.replace('api.github.com/repos', 'github.com').replace('commits', 'tree')} target="_blank" rel="noopener noreferrer" className="break-inside-avoid bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-xl p-4 hover:bg-slate-800/40 transition-all group shadow-sm hover:shadow-md block" data-id="element-4369">
                  <div className="flex items-start justify-between gap-3" data-id="element-4370">
                    <div className="flex items-start gap-3 min-w-0" data-id="element-4371">
                      <div className="bg-slate-950 p-2 rounded-lg border border-slate-800 shrink-0 mt-0.5" data-id="element-4372">
                        <GitBranchIcon size={16} className="text-slate-400 group-hover:text-indigo-400 transition-colors" data-id="element-4373" />
                      </div>
                      <div className="min-w-0" data-id="element-4374">
                        <div className="flex items-center gap-1.5 flex-wrap" data-id="element-4375">
                          <p className="text-sm font-medium text-slate-200 truncate group-hover:text-indigo-300 transition-colors" data-id="element-4376">
                            {branch.name}
                          </p>
                          {extractJiraKeys(branch.name).map(key => <JiraKeyBadge key={key} issueKey={key} jiraBaseUrl={brand.jiraBaseUrl} data-id="element-4377" />)}
                        </div>
                        <p className="text-xs text-slate-500 font-mono mt-1 flex items-center gap-1" data-id="element-4378">
                          <GitCommitIcon size={10} data-id="element-4379" />{' '}
                          {branch.commit.sha.slice(0, 7)}
                        </p>
                        {lastCommit && <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500" data-id="element-4380">
                            {lastCommit.author.avatar_url && <img src={lastCommit.author.avatar_url} alt="" className="w-4 h-4 rounded-full ring-1 ring-slate-800" data-id="element-4381" />}
                            <span className="truncate max-w-[160px]" data-id="element-4382">
                              {lastCommit.message}
                            </span>
                            <span className="shrink-0" data-id="element-4383">
                              · {getRelativeTime(lastCommit.author.date)}
                            </span>
                          </div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0" data-id="element-4384">
                      {branch.protected && <Badge variant="outline" className="gap-1 bg-amber-500/10 text-amber-400 border-amber-500/20" data-id="element-4385">
                          <ShieldIcon size={10} data-id="element-4386" /> Protected
                        </Badge>}
                    </div>
                  </div>
                </motion.a>;
        })}
          </motion.div>}
      </div>;
  };
  const renderActions = (data: typeof workflowRuns) => {
    if (actionsLoading) return <LoadingState data-id="element-4387" />;
    if (data.length === 0) return <EmptyState icon={PlayCircleIcon} message="No hay Workflow Runs recientes." data-id="element-4388" />;
    return <motion.div variants={containerVariants} initial="hidden" animate="show" className="space-y-3" data-id="element-4389">
        {data.map(run => {
        const isSuccess = run.conclusion === 'success';
        const isFailure = run.conclusion === 'failure';
        const isPending = run.status === 'in_progress' || run.status === 'queued';
        return <motion.a variants={itemVariants} key={run.id} href={run.html_url} target="_blank" rel="noopener noreferrer" className="block bg-slate-900/40 backdrop-blur-sm border border-slate-800/50 rounded-xl p-4 hover:bg-slate-800/40 transition-all group shadow-sm hover:shadow-md" data-id="element-4390">
              <div className="flex items-center gap-4" data-id="element-4391">
                <div className="shrink-0 bg-slate-950 p-2 rounded-lg border border-slate-800" data-id="element-4392">
                  {isSuccess ? <CheckCircle2Icon size={20} className="text-emerald-500" data-id="element-4393" /> : isFailure ? <XCircleIcon size={20} className="text-red-500" data-id="element-4394" /> : isPending ? <ClockIcon size={20} className="text-amber-500 animate-pulse" data-id="element-4395" /> : <AlertCircleIcon size={20} className="text-slate-500" data-id="element-4396" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-4397">
                  <div className="flex items-center justify-between gap-2 mb-1" data-id="element-4398">
                    <h3 className="text-sm font-semibold text-slate-200 group-hover:text-indigo-300 transition-colors truncate" data-id="element-4399">
                      {run.name}
                    </h3>
                    <Badge variant={isSuccess ? 'success' : isFailure ? 'error' : isPending ? 'warning' : 'default'} className="text-[10px] uppercase tracking-wider" data-id="element-4400">
                      {run.status === 'completed' ? run.conclusion : run.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-4401">
                    <span className="flex items-center gap-1.5 bg-slate-950 px-2 py-0.5 rounded border border-slate-800" data-id="element-4402">
                      <GitBranchIcon size={12} className="text-slate-400" data-id="element-4403" />{' '}
                      <span className="truncate max-w-[120px]" data-id="element-4404">
                        {run.head_branch}
                      </span>
                    </span>
                    <span className="font-mono text-slate-400" data-id="element-4405">
                      {run.head_sha}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-4406">
                      <ClockIcon size={12} data-id="element-4407" />{' '}
                      {new Date(run.run_started_at).toLocaleString()}
                    </span>
                    {run.duration && <span className="flex items-center gap-1" data-id="element-4408">
                        <ActivityIcon size={12} data-id="element-4409" />{' '}
                        {Math.floor(run.duration / 60)}m {run.duration % 60}s
                      </span>}
                  </div>
                </div>
              </div>
            </motion.a>;
      })}
      </motion.div>;
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 bg-[#020617] relative" data-id="element-4410">
      {/* Background ambient glow */}
      <div className="absolute top-0 left-1/4 w-1/2 h-64 bg-indigo-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-4411" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-4412">
        {/* Header */}
        <div className="relative pb-6 mb-8" data-id="element-4413">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4414">
            <div data-id="element-4415">
              <div className="flex items-center gap-3 mb-2" data-id="element-4416">
                <div className="relative w-10 h-10 bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/10" data-id="element-4417">
                  <motion.div animate={{
                  scale: [1, 1.1, 1]
                }} transition={{
                  repeat: Infinity,
                  duration: 2,
                  ease: 'easeInOut'
                }} className="absolute inset-0 bg-indigo-500/20 rounded-xl blur-md" data-id="element-4418" />

                  <GithubIcon size={22} className="text-slate-100 relative z-10" data-id="element-4419" />
                </div>
                <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-100 to-slate-400" data-id="element-4420">
                  {config.org || 'GitHub Intelligence'}
                </h1>
                {!selectedRepo && <Badge variant="outline" className="ml-2 mt-1 hidden sm:inline-flex" data-id="element-4421">
                    {repos.length} repos
                  </Badge>}
              </div>
              <p className="text-sm text-slate-400 flex items-center gap-2" data-id="element-4422">
                {isGlobalView ? 'Mostrando actividad de la organización' : <>
                    Filtrado por proyecto:{' '}
                    <Badge variant="outline" className="text-indigo-400 border-indigo-500/30 bg-indigo-500/10" data-id="element-4423">
                      {activeProject?.key}
                    </Badge>
                  </>}
              </p>
            </div>

            <div className="flex items-center gap-4" data-id="element-4424">
              {renderRateLimit()}
              <RefreshButton onRefresh={refresh} isRefreshing={loading} data-id="element-4425" />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" data-id="element-4426" />
        </div>

        {/* Error Banner */}
        <AnimatePresence data-id="element-4427">
          {error && !dismissedError && <motion.div initial={{
          opacity: 0,
          y: -20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -20,
          scale: 0.95
        }} className="mb-8 p-4 bg-red-950/40 border border-red-900/50 rounded-2xl flex items-start gap-3 text-red-400 backdrop-blur-md shadow-lg shadow-red-900/20" data-id="element-4428">
              <AlertCircleIcon size={20} className="shrink-0 mt-0.5" data-id="element-4429" />
              <div className="flex-1" data-id="element-4430">
                <h4 className="text-sm font-bold mb-1 text-red-300" data-id="element-4431">
                  Error de Conexión
                </h4>
                <p className="text-sm opacity-90" data-id="element-4432">{error}</p>
              </div>
              <button onClick={() => setDismissedError(true)} className="p-1 hover:bg-red-900/50 rounded-lg transition-colors" data-id="element-4433">
                <XIcon size={16} data-id="element-4434" />
              </button>
            </motion.div>}
        </AnimatePresence>

        {/* Main Content Area */}
        {!selectedRepo ? renderOrgOverview() : renderRepoDetail()}
      </div>

      {/* PR Review Panel Overlay */}
      <AnimatePresence data-id="element-4435">
        {reviewingPR && <PRReviewPanel pr={reviewingPR} onClose={() => setReviewingPR(null)} data-id="element-4436" />}
      </AnimatePresence>

      {/* README Side Panel / Fullscreen Overlay */}
      <AnimatePresence data-id="element-4437">
        {expandedReadme && <>
            {/* Backdrop for side panel mode */}
            {!readmeFullscreen && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => {
          setExpandedReadme(null);
          setReadmeFullscreen(false);
        }} className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" data-id="element-4438" />}

            <motion.div initial={{
          x: '100%',
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: '100%',
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className={`fixed z-50 bg-slate-900 border-slate-800 shadow-2xl flex flex-col overflow-hidden ${readmeFullscreen ? 'inset-0 w-full h-full' : 'top-0 right-0 bottom-0 w-full max-w-[600px] border-l'}`} data-id="element-4439">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md shrink-0" data-id="element-4440">
                <div className="flex items-center gap-3 min-w-0" data-id="element-4441">
                  <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20 shrink-0" data-id="element-4442">
                    <FileTextIcon size={18} className="text-indigo-400" data-id="element-4443" />
                  </div>
                  <div className="min-w-0" data-id="element-4444">
                    <div className="flex items-center gap-2" data-id="element-4445">
                      <h3 className="text-sm font-bold text-slate-100" data-id="element-4446">
                        README.md
                      </h3>
                      {readmeMeta?.isOutdated && <Badge variant="warning" className="text-[9px] py-0 px-1.5 h-4 flex items-center gap-1" data-id="element-4447">
                          <AlertCircleIcon size={8} data-id="element-4448" /> Desfasado
                        </Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5" data-id="element-4449">
                      <p className="text-xs text-slate-500 font-mono truncate" data-id="element-4450">
                        {expandedReadme}
                      </p>
                      {readmeMeta && <>
                          <span className="text-slate-700" data-id="element-4451">•</span>
                          <p className="text-[10px] text-slate-400 flex items-center gap-1 shrink-0" title={`Último commit por ${readmeMeta.author}`} data-id="element-4452">
                            <ClockIcon size={10} data-id="element-4453" />{' '}
                            {getRelativeTime(readmeMeta.date)}
                          </p>
                        </>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0" data-id="element-4454">
                  {!readmeLoading && !readmeError && readmeContent && <SummarizeButton content={readmeContent} title={`README de ${expandedReadme}`} contextType="README de repositorio" variant="header" className="mr-1" data-id="element-4455" />}
                  <button onClick={() => {
                setReadmeSearchActive(!readmeSearchActive);
                if (!readmeSearchActive) {
                  setTimeout(() => readmeSearchInputRef.current?.focus(), 50);
                } else {
                  setReadmeSearch('');
                }
              }} className={`p-2 rounded-lg transition-colors ${readmeSearchActive ? 'text-indigo-400 bg-indigo-500/10' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-800'}`} title="Buscar en README (Ctrl+F)" data-id="element-4456">
                    <SearchIcon size={16} data-id="element-4457" />
                  </button>
                  <button onClick={() => setReadmeFullscreen(!readmeFullscreen)} className="p-2 text-slate-400 hover:text-indigo-400 hover:bg-slate-800 rounded-lg transition-colors" title={readmeFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'} data-id="element-4458">
                    {readmeFullscreen ? <Minimize2Icon size={16} data-id="element-4459" /> : <Maximize2Icon size={16} data-id="element-4460" />}
                  </button>
                  <button onClick={() => {
                setExpandedReadme(null);
                setReadmeFullscreen(false);
              }} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors" title="Cerrar" data-id="element-4461">
                    <XIcon size={16} data-id="element-4462" />
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <AnimatePresence data-id="element-4463">
                {readmeSearchActive && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.15
            }} className="overflow-hidden border-b border-slate-800 bg-slate-900/60" data-id="element-4464">
                    <div className="flex items-center gap-3 px-6 py-3" data-id="element-4465">
                      <div className="relative flex-1" data-id="element-4466">
                        <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-4467" />

                        <input ref={readmeSearchInputRef} type="text" placeholder="Buscar en README..." value={readmeSearch} onChange={e => {
                    setReadmeSearch(e.target.value);
                    setReadmeMatchIndex(0);
                  }} onKeyDown={e => {
                    if (e.key === 'Enter' && readmeMatchCount > 0) {
                      e.preventDefault();
                      if (e.shiftKey) {
                        setReadmeMatchIndex(prev => (prev - 1 + readmeMatchCount) % readmeMatchCount);
                      } else {
                        setReadmeMatchIndex(prev => (prev + 1) % readmeMatchCount);
                      }
                    }
                    if (e.key === 'Escape') {
                      setReadmeSearchActive(false);
                      setReadmeSearch('');
                    }
                  }} className="w-full bg-slate-950 border border-slate-800 rounded-lg py-1.5 pl-9 pr-3 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all" data-id="element-4468" />
                      </div>
                      {readmeSearch && <span className="text-xs text-slate-400 font-mono whitespace-nowrap" data-id="element-4469">
                          {readmeMatchCount > 0 ? <>
                              {readmeMatchIndex + 1} / {readmeMatchCount}
                            </> : <span className="text-slate-600" data-id="element-4470">
                              Sin resultados
                            </span>}
                        </span>}
                      {readmeMatchCount > 1 && <div className="flex items-center gap-1" data-id="element-4471">
                          <button onClick={() => setReadmeMatchIndex(prev => (prev - 1 + readmeMatchCount) % readmeMatchCount)} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-md transition-colors" title="Anterior (Shift+Enter)" data-id="element-4472">
                            <ChevronDownIcon size={14} className="rotate-180" data-id="element-4473" />
                          </button>
                          <button onClick={() => setReadmeMatchIndex(prev => (prev + 1) % readmeMatchCount)} className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-md transition-colors" title="Siguiente (Enter)" data-id="element-4474">
                            <ChevronDownIcon size={14} data-id="element-4475" />
                          </button>
                        </div>}
                      <button onClick={() => {
                  setReadmeSearchActive(false);
                  setReadmeSearch('');
                }} className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded-md transition-colors" data-id="element-4476">
                        <XIcon size={14} data-id="element-4477" />
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar" data-id="element-4478">
                <div ref={readmeContentRef} className={readmeFullscreen ? 'max-w-4xl mx-auto' : ''} data-id="element-4479">
                  {readmeLoading ? <div className="flex flex-col items-center justify-center py-24 text-slate-500 gap-4" data-id="element-4480">
                      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-4481" />
                      <span className="text-sm font-medium" data-id="element-4482">
                        Cargando README...
                      </span>
                    </div> : readmeError ? <div className="flex flex-col items-center justify-center py-24 text-slate-500 gap-3" data-id="element-4483">
                      <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-xl flex items-center justify-center mb-2" data-id="element-4484">
                        <AlertCircleIcon size={24} data-id="element-4485" />
                      </div>
                      <span className="text-sm font-medium text-slate-300" data-id="element-4486">
                        {readmeError}
                      </span>
                    </div> : <div className="markdown-body" style={{
                backgroundColor: 'transparent'
              }} data-id="element-4487">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-4488">
                        {readmeContent}
                      </ReactMarkdown>
                    </div>}
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}
function LoadingState() {
  return <div className="space-y-4" data-id="element-4489">
      {[1, 2, 3].map(i => <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-5 relative overflow-hidden" data-id="element-4490">
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-slate-800/20 to-transparent" data-id="element-4491" />
          <div className="flex items-start gap-4 relative z-10" data-id="element-4492">
            <div className="w-8 h-8 bg-slate-800/50 rounded-lg" data-id="element-4493" />
            <div className="flex-1 space-y-3" data-id="element-4494">
              <div className="h-5 bg-slate-800/50 rounded-md w-3/4" data-id="element-4495" />
              <div className="flex gap-3" data-id="element-4496">
                <div className="h-4 bg-slate-800/50 rounded-md w-24" data-id="element-4497" />
                <div className="h-4 bg-slate-800/50 rounded-md w-32" data-id="element-4498" />
              </div>
            </div>
          </div>
        </div>)}
    </div>;
}
function EmptyState({
  icon: Icon,
  message
}: {
  icon: any;
  message: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="flex flex-col items-center justify-center py-24 text-center border border-dashed border-slate-800/60 rounded-3xl bg-slate-900/20 backdrop-blur-sm" data-id="element-4499">
      <motion.div animate={{
      y: [0, -10, 0]
    }} transition={{
      repeat: Infinity,
      duration: 3,
      ease: 'easeInOut'
    }} className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center mb-6 border border-slate-800 shadow-xl shadow-slate-950/50 relative" data-id="element-4500">
        <div className="absolute inset-0 bg-indigo-500/10 rounded-2xl blur-md" data-id="element-4501" />
        <Icon size={32} className="text-slate-500 relative z-10" data-id="element-4502" />
      </motion.div>
      <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4503">Sin resultados</h3>
      <p className="text-slate-400 text-sm max-w-sm" data-id="element-4504">{message}</p>
    </motion.div>;
}